import React, { useState, useMemo } from 'react';
import './SensorDevicePanel.css';
import InsighioSensorDetail from './InsighioSensorDetail';
import { sensorEntries } from '../Map/weatherStationCard';
import { Cloud, Droplets, Wind, Waves, FlaskConical, TreePine, Radio, ChevronRight, Circle, CheckCircle2 } from 'lucide-react';

// ─── Category registry ────────────────────────────────────────────────────────
// Priority: sensorType.code (DB) → name-pattern fallback
const CATEGORIES = [
    {
        code: 'WEATHER',
        label: { en: 'Weather', tr: 'Hava İstasyonu' },
        Icon: Cloud,
        accent: '#0ea5e9',
        namePatterns: [/weather/i, /meteo/i, /station/i, /istasyon/i],
    },
    {
        code: 'WATER_QUALITY',
        label: { en: 'Water Quality', tr: 'Su Kalitesi' },
        Icon: Waves,
        accent: '#6366f1',
        namePatterns: [/water.?quality/i, /wq/i, /exo/i, /ysi/i, /sonde/i],
    },
    {
        code: 'FLOW_METER',
        label: { en: 'Flow', tr: 'Akış Ölçer' },
        Icon: Wind,
        accent: '#10b981',
        namePatterns: [/flow/i, /liter/i, /litre/i, /sayac/i, /meter/i],
    },
    {
        code: 'SOIL_VWC',
        label: { en: 'Soil Moisture', tr: 'Toprak Nemi' },
        Icon: Droplets,
        accent: '#f59e0b',
        namePatterns: [/vwc/i, /soil/i, /toprak/i, /moisture/i],
    },
    {
        code: 'SOIL_NPK',
        label: { en: 'Soil NPK', tr: 'Toprak NPK' },
        Icon: FlaskConical,
        accent: '#8b5cf6',
        namePatterns: [/npk/i, /nitrogen/i, /azot/i],
    },
    {
        code: 'DENDROMETER',
        label: { en: 'Dendrometer', tr: 'Drenometre' },
        Icon: TreePine,
        accent: '#78716c',
        namePatterns: [/dendro/i, /stem/i, /gövde/i],
    },
];

const OTHER = {
    code: '__OTHER__',
    label: { en: 'Other', tr: 'Diğer' },
    Icon: Radio,
    accent: '#6b7280',
};

function resolveCategory(sensor) {
    // 1. DB sensorType.code (most reliable)
    if (sensor.sensorType?.code) {
        const found = CATEGORIES.find(c => c.code === sensor.sensorType.code);
        if (found) return found;
    }
    // 2. Legacy sensor.type string
    if (sensor.type) {
        const found = CATEGORIES.find(c => c.code === sensor.type);
        if (found) return found;
    }
    // 3. Name pattern fallback
    const name = sensor.name || '';
    for (const cat of CATEGORIES) {
        if (cat.namePatterns?.some(p => p.test(name))) return cat;
    }
    return OTHER;
}

// ─── Measurement display name lookup ─────────────────────────────────────────
const MEAS_NAMES = {
    'A.Temperature':             'Air Temperature',
    'board_humidity':            'Relative Humidity',
    'A_Pressure':                'Pressure',
    'Precipitation':             'Rainfall',
    'meter_0_1_east_wind_speed': 'Wind Speed (E)',
    'meter_0_1_north_wind_speed':'Wind Speed (N)',
    'SolarRadiation':            'Solar Radiation',
    'LeafWetness':               'Leaf Wetness',
    'BatteryVoltage':            'Battery Voltage',
    'RSSI':                      'Signal Strength',
    'Conductivity':              'Electrical Conductivity',
    'DO':                        'Dissolved Oxygen',
    'ORP':                       'ORP',
    'pH':                        'pH',
    'Salinity':                  'Salinity',
    'Turbidity':                 'Turbidity',
    'Liter':                     'Water Consumption',
    'Litre':                     'Water Consumption',
    'FlowRate':                  'Flow Rate',
    'VWC_15cm':                  'VWC 15 cm',
    'VWC_50cm':                  'VWC 50 cm',
    'S.Temperature_15cm':        'Soil Temp 15 cm',
    'S.Temperature_50cm':        'Soil Temp 50 cm',
    'S.Conductivity_15cm':       'Soil EC 15 cm',
    'S.Conductivity_50cm':       'Soil EC 50 cm',
};

const MEAS_UNITS = {
    'A.Temperature': '°C', 'board_humidity': '%', 'A_Pressure': 'hPa',
    'Precipitation': 'mm', 'meter_0_1_east_wind_speed': 'm/s',
    'meter_0_1_north_wind_speed': 'm/s', 'SolarRadiation': 'W/m²',
    'LeafWetness': '%', 'BatteryVoltage': 'V', 'RSSI': 'dBm',
    'Conductivity': 'mS/cm', 'DO': 'mg/L', 'ORP': 'mV',
    'pH': '', 'Salinity': 'ppt', 'Turbidity': 'NTU',
    'Liter': 'L', 'Litre': 'L', 'FlowRate': 'L/min',
    'VWC_15cm': '%', 'VWC_50cm': '%',
    'S.Temperature_15cm': '°C', 'S.Temperature_50cm': '°C',
    'S.Conductivity_15cm': 'dS/m', 'S.Conductivity_50cm': 'dS/m',
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function SensorDevicePanel({ sensors = [], language = 'en', onSensorClick, selectedSensorId = null }) {
    const isEN = language === 'en';

    // Which categories are open (accordion) — all closed on first render
    const [openCats, setOpenCats] = useState(() => new Set());
    // Selected sensor id for right panel — controlled externally or internally
    const [internalSelectedId, setInternalSelectedId] = useState(null);

    const selectedId = selectedSensorId ?? internalSelectedId;

    // Group sensors by category
    const groups = useMemo(() => {
        const map = new Map();
        for (const s of sensors) {
            const cat = resolveCategory(s);
            if (!map.has(cat.code)) map.set(cat.code, { cat, sensors: [] });
            map.get(cat.code).sensors.push(s);
        }
        // Sort: known categories first in defined order, then OTHER
        const ordered = [];
        for (const cat of [...CATEGORIES, OTHER]) {
            if (map.has(cat.code)) ordered.push(map.get(cat.code));
        }
        return ordered;
    }, [sensors]);

    const selectedSensor = sensors.find(s => s.id === selectedId) ?? null;
    const selectedCat = selectedSensor ? resolveCategory(selectedSensor) : null;

    const toggleCat = (code) => setOpenCats(prev => {
        const next = new Set(prev);
        next.has(code) ? next.delete(code) : next.add(code);
        return next;
    });

    const handleSelect = (sensor) => {
        setInternalSelectedId(sensor.id);
        if (onSensorClick) onSensorClick(sensor);
    };

    if (sensors.length === 0) return (
        <div className="sdp-empty">
            <Radio size={24} strokeWidth={1.5} />
            <p>{isEN ? 'No sensors linked to this field.' : 'Bu tarlaya bağlı sensör yok.'}</p>
        </div>
    );

    return (
        <div className="sdp-list-only">
            {groups.map(({ cat, sensors: catSensors }) => {
                const isOpen = openCats.has(cat.code);
                const CatIcon = cat.Icon;
                return (
                    <div key={cat.code} className="sdp-group">
                        <button
                            className="sdp-group-header"
                            onClick={() => toggleCat(cat.code)}
                            style={{ '--cat-accent': cat.accent }}
                        >
                            <ChevronRight size={10} className={`sdp-chevron ${isOpen ? 'sdp-chevron-open' : ''}`} />
                            <CatIcon size={12} className="sdp-cat-icon" />
                            <span className="sdp-cat-name">
                                {isEN ? cat.label.en : cat.label.tr}
                            </span>
                            <span className="sdp-cat-badge">{catSensors.length}</span>
                        </button>

                        {isOpen && (
                            <div className="sdp-sensor-list">
                                {catSensors.flatMap(s => sensorEntries(s, isEN ? 'en' : 'tr')).map(({ key, label, sensor }) => {
                                    const hasData = sensor.latestData && Object.keys(sensor.latestData).length > 0;
                                    const isSelected = sensor.id === selectedId;
                                    return (
                                        <button
                                            key={key}
                                            className={`sdp-sensor-row ${isSelected ? 'sdp-sensor-selected' : ''}`}
                                            style={{ '--cat-accent': cat.accent }}
                                            onClick={() => handleSelect(sensor)}
                                        >
                                            {isSelected
                                                ? <CheckCircle2 size={10} className="sdp-check" />
                                                : <Circle size={10} className="sdp-check" />
                                            }
                                            <span className="sdp-sensor-info">
                                                <span className="sdp-sensor-name">{label}</span>
                                            </span>
                                            {hasData && (
                                                <span className="sdp-live-dot" title={isEN ? 'Live' : 'Canlı'} />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
